import { memo, useRef, useCallback } from 'react';
import { BaseFormModal } from '@/components/BaseFormModal';
import { Dropdownlist } from '@/components/Dropdownlist';
import { MultiSelect } from '@/components/MultiSelect';
import { DateTimePicker } from '@/components/DateTimePicker';

interface EditingNote {
  id: number | null;
  displayId: number | null;
  recipientType: string;
  npsdClient: number | null;
  selectedSiteIds: number[];
  dateTime: Date;
  onBehalfOf: number | null;
  location: string;
  subject: string;
  notes: string;
  attachmentList: any[];
  resizedImageList: any[];
  attachmentInfoList: any[];
}

interface NoteFormModalProps {
  open: boolean;
  isNewNote: boolean;
  isSaving: boolean;
  isUploadingAttachment: boolean;
  editingNote: EditingNote;
  recipientTypeItems: Array<{ label: string; value: string }>;
  clientItems: Array<{ label: string; value: number }>;
  clientSites: Array<{ id: number; siteName: string }>;
  onBehalfOfItems: Array<{ label: string; value: number | null }>;
  canSetOnBehalfOf: boolean;
  isDateTimePickerOpen: boolean;
  theme: string;
  onSave: () => void;
  onCancel: () => void;
  onRecipientTypeChange: (value: string | number | null) => void;
  onClientChange: (value: string | number | null) => void;
  onSiteIdsChange: (values: any[]) => void;
  onOnBehalfOfChange: (value: string | number | null) => void;
  onDateTimeChange: (value: Date | null) => void;
  onOpenDateTimePicker: () => void;
  onCloseDateTimePicker: () => void;
  onDateTimePickerOpenChange: (open: boolean) => void;
  onEditingNoteChange: (updater: (prev: EditingNote) => EditingNote) => void;
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveAttachment: (attachmentId: number) => void;
  getResizedImageUrl: (attachmentId: number) => string;
}

export const NoteFormModal = memo(function NoteFormModal({
  open,
  isNewNote,
  isSaving,
  isUploadingAttachment,
  editingNote,
  recipientTypeItems,
  clientItems,
  clientSites,
  onBehalfOfItems,
  canSetOnBehalfOf,
  isDateTimePickerOpen,
  theme,
  onSave,
  onCancel,
  onRecipientTypeChange,
  onClientChange,
  onSiteIdsChange,
  onOnBehalfOfChange,
  onDateTimeChange,
  onOpenDateTimePicker,
  onCloseDateTimePicker,
  onDateTimePickerOpenChange,
  onEditingNoteChange,
  onFileSelect,
  onRemoveAttachment,
  getResizedImageUrl,
}: NoteFormModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleTriggerFileUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <BaseFormModal
      open={open}
      title={isNewNote ? 'Add Note' : 'Modify Note'}
      dialogClass="kstc-dialog--form kstc-dialog--notes"
      bodyClass="kstc-body--scroll"
      saveText={
        isSaving
          ? 'Saving...'
          : isNewNote
            ? 'Add Note'
            : 'Save Changes'
      }
      saveDisabled={isSaving}
      busy={isSaving}
      onSave={onSave}
      onCancel={onCancel}
    >
      {/* Note ID (only for modify) */}
      {!isNewNote && (
        <div className="kstc-form-group">
          <label className="kstc-label">Note ID</label>
          <div className="kstc-display-value">
            #{editingNote.displayId}
          </div>
        </div>
      )}

      {/* Recipients */}
      <div className="kstc-form-group">
        <label className="kstc-label kstc-label--required">
          Recipients
        </label>
        <Dropdownlist
          value={editingNote.recipientType}
          items={recipientTypeItems}
          labelKey="label"
          valueKey="value"
          placeholder="Select Recipients..."
          searchable={false}
          onChange={onRecipientTypeChange}
        />
      </div>

      {/* Client (only when recipient type is 'c') */}
      {editingNote.recipientType === 'c' && (
        <div className="kstc-form-group">
          <label className="kstc-label kstc-label--required">
            Client
          </label>
          <Dropdownlist
            value={editingNote.npsdClient}
            items={clientItems}
            labelKey="label"
            valueKey="value"
            placeholder="Select Client..."
            searchable={clientItems.length > 5}
            onChange={onClientChange}
          />
        </div>
      )}

      {/* Sites (only when recipient type is 'c' and client selected) */}
      {editingNote.recipientType === 'c' && editingNote.npsdClient && (
        <div className="kstc-form-group">
          <label className="kstc-label">Sites</label>
          <MultiSelect
            value={editingNote.selectedSiteIds}
            items={clientSites}
            labelKey="siteName"
            valueKey="id"
            placeholder="Select Sites..."
            onChange={onSiteIdsChange}
          />
        </div>
      )}

      {/* Date/Time */}
      <div className="kstc-form-group">
        <label className="kstc-label kstc-label--required">
          Date/Time
        </label>
        <DateTimePicker
          value={editingNote.dateTime}
          type="datetime"
          format="MM/DD/YYYY HH:mm"
          valueType="date"
          clearable={false}
          appendToBody={true}
          popupClass={theme}
          open={isDateTimePickerOpen}
          onFocus={onOpenDateTimePicker}
          onClose={onCloseDateTimePicker}
          onChange={onDateTimeChange}
          onOpenChange={onDateTimePickerOpenChange}
        />
      </div>

      {/* On Behalf Of */}
      {canSetOnBehalfOf && (
        <div className="kstc-form-group">
          <label className="kstc-label">On Behalf Of</label>
          <Dropdownlist
            value={editingNote.onBehalfOf}
            items={onBehalfOfItems}
            labelKey="label"
            valueKey="value"
            placeholder="None"
            searchable={onBehalfOfItems.length > 5}
            onChange={onOnBehalfOfChange}
          />
        </div>
      )}

      {/* Location */}
      <div className="kstc-form-group">
        <label className="kstc-label">Location</label>
        <input
          type="text"
          className="kstc-input"
          value={editingNote.location}
          placeholder="Enter location"
          maxLength={255}
          onChange={(e) =>
            onEditingNoteChange((prev) => ({
              ...prev,
              location: e.target.value,
            }))
          }
        />
      </div>

      {/* Subject */}
      <div className="kstc-form-group">
        <label className="kstc-label kstc-label--required">Subject</label>
        <input
          type="text"
          className="kstc-input"
          value={editingNote.subject}
          placeholder="Enter subject"
          maxLength={255}
          onChange={(e) =>
            onEditingNoteChange((prev) => ({
              ...prev,
              subject: e.target.value,
            }))
          }
        />
      </div>

      {/* Notes */}
      <div className="kstc-form-group">
        <label className="kstc-label kstc-label--required">Notes</label>
        <textarea
          className="kstc-input kstc-textarea"
          value={editingNote.notes}
          rows={5}
          placeholder="Enter notes..."
          onChange={(e) =>
            onEditingNoteChange((prev) => ({
              ...prev,
              notes: e.target.value,
            }))
          }
        />
      </div>

      {/* Attachments */}
      <div className="kstc-form-group">
        <label className="kstc-label">Attachments</label>
        <div className="kstc-attachments">
          <button
            type="button"
            className="kstc-btn kstc-btn--secondary"
            onClick={handleTriggerFileUpload}
            disabled={isUploadingAttachment}
          >
            <i className="fa fa-plus"></i> Add Attachment
          </button>
          {isUploadingAttachment && (
            <span className="kstc-upload-status">Uploading...</span>
          )}
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={onFileSelect}
            accept="image/*,.pdf,.doc,.docx,.txt"
            multiple
          />

          {editingNote.attachmentList &&
          editingNote.attachmentList.length > 0 ? (
            <div className="kstc-attachments-list">
              {editingNote.attachmentList.map(
                (attachment: any, index: number) => (
                  <div
                    className="kstc-attachment-item"
                    key={attachment.id || index}
                  >
                    {getResizedImageUrl(attachment.id) && (
                      <div className="kstc-attachment-preview">
                        <img
                          src={getResizedImageUrl(attachment.id)}
                          alt="Preview"
                        />
                      </div>
                    )}
                    <div className="kstc-attachment-info">
                      <a
                        href={attachment.signedURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="kstc-attachment-link"
                      >
                        {attachment.title ||
                          attachment.filename ||
                          'Attachment'}
                      </a>
                    </div>
                    <button
                      type="button"
                      className="kstc-btn-icon kstc-btn-icon--danger"
                      onClick={() => onRemoveAttachment(attachment.id)}
                      title="Remove"
                    >
                      <i className="fa fa-trash"></i>
                    </button>
                  </div>
                )
              )}
            </div>
          ) : (
            <div className="kstc-no-attachments">No attachments</div>
          )}
        </div>
      </div>
    </BaseFormModal>
  );
});
